import { useMemo } from 'react';
import ActionStatusText from '../../components/ui/ActionStatusText';
import ErrorMessage from '../../components/ui/ErrorMessage';
import { useModalNavigation } from '../../context/ModalControllerContext';
import { buildExercisePayload } from '../../helpers';
import { isExerciseFormIncomplete } from '../../helpers/domain';
import useSaveExercise from '../../hooks/data/exercises/useSaveExercise';
import useExerciseForm from '../../hooks/exercise/useExerciseForm';
import ExerciseTypeSearch from '../components/ExerciseTypeSearch';
import ModalHeader from '../components/ModalHeader';

const ModalExercise = ({ currentDay }) => {
    const { setView, close, closeWithDelay } = useModalNavigation();

    const {
        form,
        handleChange,
        handleSelect,
        clearSearch,
        filteredResults,
        dropdownRef,
        showSuggestions,
        showInvalidMessage,
        exerciseTypes,
        resetForm,
        isLoading: isLoadingTypes,
        isError: isErrorTypes
    } = useExerciseForm({ setView });

    const { saveExercise, isSaving, isSuccess, isError, error } = useSaveExercise(currentDay);

    const isFormIncomplete = useMemo(
        () => isExerciseFormIncomplete(form, exerciseTypes),
        [form, exerciseTypes]
    );

    const handleSubmit = async () => {
        try {
            if (isFormIncomplete) return;

            const payload = buildExercisePayload({
                isCustom: false,
                form
            });

            await saveExercise(payload);

            resetForm();
            closeWithDelay();
        } catch (err) {
            console.error('Failed to submit exercise:', err);
        }
    };

    const handleResultSelect = (result) => {
        if (result.kind === 'favorite_session') {
            setView('editExercise', {
                actionType: 'favorite',
                entry: result.data
            });
            return;
        }

        handleSelect(result);
    };

    const isSteps = Number(form.exercise_type) === 2;

    const isLoading = isSaving || isLoadingTypes;
    const hasError = isError || isErrorTypes;
    const isLocked = isLoading || isSuccess;

    return (
        <>
            <ModalHeader
                title="Lägg till motion"
                onClose={close}
                showBack={false}
                disableButtons={isLocked}
            />

            <div className="modal-body exercise">
                {hasError ? (
                    <ErrorMessage
                        message={error?.message || 'Ett fel uppstod vid hämtning eller sparande.'}
                    />
                ) : (
                    <div className="exercise-input-wrapper" ref={dropdownRef}>
                        {/* --- SEARCH FIELD --- */}
                        <ExerciseTypeSearch
                            form={form}
                            filteredResults={filteredResults}
                            showSuggestions={showSuggestions}
                            showInvalidMessage={showInvalidMessage}
                            handleChange={handleChange}
                            handleSelect={handleResultSelect}
                            clearSearch={clearSearch}
                            isLocked={isLocked}
                        />
                        {/* --- STEPS / DURATION --- */}
                        {isSteps ? (
                            <div className="input-with-label">
                                <span className="inline-label">Antal steg:</span>
                                <input
                                    type="number"
                                    name="exercise_steps"
                                    className="modal-input steps-input"
                                    min="1"
                                    step="1"
                                    value={form.exercise_steps}
                                    onChange={handleChange}
                                    disabled={isLocked}
                                />
                            </div>
                        ) : (
                            <div className="input-with-label time-input">
                                <span className="inline-label">Tid (min):</span>
                                <input
                                    type="number"
                                    name="exercise_duration"
                                    className="modal-input duration-input"
                                    min="1"
                                    step="1"
                                    value={form.exercise_duration}
                                    onChange={handleChange}
                                    disabled={isLocked}
                                />
                            </div>
                        )}
                        {/* --- TIME --- */}
                        <div className="input-with-label time-input">
                            <span className="inline-label">Klockslag:</span>
                            <input
                                type="time"
                                name="exercise_time"
                                className="modal-input time-input"
                                value={form.exercise_time}
                                onChange={handleChange}
                                disabled={isLocked}
                            />
                        </div>
                        {/* --- COMMENT --- */}
                        <div className="input-with-label">
                            <span className="inline-label">Kommentar:</span>
                            <input
                                type="text"
                                name="exercise_comment"
                                className="modal-input comment-input"
                                value={form.exercise_comment}
                                onChange={handleChange}
                                disabled={isLocked}
                                placeholder={'Valfritt'}
                            />
                        </div>
                    </div>
                )}
            </div>

            <div className="modal-footer exercise">
                <button
                    type="button"
                    className="btn-green bold-title"
                    onClick={handleSubmit}
                    disabled={isLocked || isFormIncomplete}
                >
                    <ActionStatusText
                        isProcessing={isSaving}
                        isCompleted={isSuccess}
                        defaultLabel="Spara motion"
                        successLabel="Motion sparad!"
                    />
                </button>

                <button
                    type="button"
                    className="marked-border-btn bold-title"
                    onClick={() => setView('ownExercise')}
                    disabled={isLocked}
                >
                    Lägg till egen kaloriberäknad motion
                </button>
            </div>
        </>
    );
};

export default ModalExercise;
